// Datenzugriff für die Dashboard-Karten (Block 2). Liest ausschließlich
// bestehende Entity-Endpunkte (kein eigener Dashboard-Endpunkt) — die Karten
// aggregieren clientseitig. Aktuell: offene Schäden (AP-S, DD6/DD8) und
// meine/Team-Kunden (AP-K, DD11/DD8).
import { apiFetch } from "../utils/apiFetch";

/** „Meine"/„Team"-Scope der Daten-Karten (DD8 — kein „Alle"). */
export type DashScope = "own" | "team";

/** Schlanke Sicht auf einen offenen Schaden für die Schäden-Karte. */
export interface DashboardDamage {
    id: number;
    /** Schadennummer (intern bevorzugt, sonst extern). */
    damageNbr: string;
    /** Kurztitel/Bezeichnung des Schadens. */
    title: string;
    /** Schadendatum (ISO) — für `deDate`. */
    damageDate: string | null;
    /** Name des zugehörigen Kunden (DD6) — „Nachname, Vorname" bzw. Firma. */
    contactName: string;
    /** Kontakt-Id (für eine evtl. spätere Verlinkung auf den Kunden). */
    contactId: number | null;
}

/** Roh-Form, wie der `GET /damage`-Endpunkt einen Schaden liefert (Teilmenge). */
interface RawDamage {
    id: number;
    damageNbr?: string | null;
    externalDamageNbr?: string | null;
    title?: string | null;
    damageDate?: string | null;
    contact?: {
        id?: number | null;
        lastname?: string | null;
        firstName?: string | null;
        company?: string | null;
        displayName?: string | null;
    } | null;
    // RLS-/Ownership-Metadaten (vom Entity-Endpunkt mitgeliefert) — für den
    // clientseitigen Meine/Team-Filter (DD8). Der Damage-List-Endpunkt bietet
    // keinen eigenen `?scope`-Parameter (anders als Kontakte), daher filtern
    // wir hier nach Eigentümer/Team.
    ownerUserId?: string | null;
    ownerTeamId?: string | null;
    createdByUserId?: string | null;
}

/** Kunden-Anzeigename aus der Schaden-Kontaktrelation (DD6). */
const contactName = (c: RawDamage["contact"]): string => {
    if (!c) return "";
    if (c.displayName) return String(c.displayName);
    const parts = [c.lastname, c.firstName].filter(Boolean);
    if (parts.length) return parts.join(", ");
    return c.company ? String(c.company) : "";
};

/** Gehört der Schaden in den gewählten Scope (Meine = ich, Team = mein Team)? */
const matchesScope = (
    d: RawDamage,
    scope: DashScope,
    ctx: { userId: string | null; teammates: string[]; teamIds: string[] },
): boolean => {
    if (scope === "own") {
        if (!ctx.userId) return false;
        return d.ownerUserId === ctx.userId || d.createdByUserId === ctx.userId;
    }
    // team: Schaden-Eigentümer ist ein Teamkollege ODER das Owner-Team ist meines.
    const ownerInTeam = !!d.ownerUserId && ctx.teammates.includes(d.ownerUserId);
    const creatorInTeam = !!d.createdByUserId && ctx.teammates.includes(d.createdByUserId);
    const ownerTeamMine = !!d.ownerTeamId && ctx.teamIds.includes(d.ownerTeamId);
    return ownerInTeam || creatorInTeam || ownerTeamMine;
};

export interface OpenDamagesResult {
    /** Top-N offene Schäden im gewählten Scope (für die Liste). */
    items: DashboardDamage[];
    /** Anzahl offener Schäden im gewählten Scope (KPI). */
    count: number;
}

/** Schlanke Sicht auf einen Kunden für die Meine-Kunden-Karte (AP-K, DD11). */
export interface DashboardContact {
    id: number;
    /** Anzeigename („Nachname, Vorname" bzw. Firma). */
    displayName: string;
    /** Kontakttyp (z. B. „Privat"/„Firma") — als dezente Zeile-2-Info. */
    typeLabel: string;
    /** Letzte Aktivität (ISO) — Sortierkriterium (DD11). */
    lastActivity: string | null;
}

/** Roh-Form, wie der `GET /contact`-Endpunkt einen Kontakt liefert (Teilmenge). */
interface RawContact {
    id: number;
    displayName?: string | null;
    lastname?: string | null;
    firstName?: string | null;
    company?: string | null;
    type?: { type?: string | null } | null;
    // „Letzte Aktivität" — der Kontakt-List-Endpunkt liefert die vollen
    // Entity-Spalten je Zeile; `lastModified` (sonst `created`) ist das beste
    // verfügbare Aktivitäts-Signal. Es gibt keinen serverseitigen
    // `?sort=lastActivity` (Allowlist nur Name/Ort/…), daher sortieren wir
    // clientseitig (DD11).
    lastModified?: string | null;
    created?: string | null;
}

export interface MyContactsResult {
    /** Top-N Kunden des Scope, sortiert nach letzter Aktivität (DD11). */
    items: DashboardContact[];
    /** Gesamtzahl der Kunden im Scope (KPI) — aus `X-Total-Count`. */
    count: number;
}

/** Klassifizierter Abwesenheits-Typ (AP-A, DD10) — spiegelt den Backend-`AbsenceKind`. */
export type AbsenceKind = "urlaub" | "krank" | "homeoffice" | "abwesend";

/** Ein heute abwesender Mitarbeiter (AP-A, DD10). */
export interface DashboardAbsence {
    /** better-auth user.id (sofern auflösbar), sonst null. */
    userId: string | null;
    /** Postfach-Adresse, aus der die Abwesenheit gelesen wurde. */
    mailbox: string;
    /** Anzeigename des Mitarbeiters. */
    name: string;
    /** Klassifizierter Abwesenheits-Typ. */
    type: AbsenceKind;
    /** Ende der Abwesenheit ("bis wann"), ISO-String oder null. */
    until: string | null;
}

/** Präsenz-Status: anwesend ODER ein Abwesenheits-Typ (spiegelt Backend). */
export type PresenceStatus = "present" | AbsenceKind;

/** Ein Mitarbeiter im vollständigen Roster der An-/Abwesenheiten-Karte. */
export interface PersonPresence {
    /** better-auth user.id (für das Profilfoto), sonst null. */
    userId: string | null;
    /** Anzeigename (Vor- + Nachname). */
    name: string;
    /** Vorname — Sortierschlüssel der Karte. */
    firstName: string | null;
    /** Kürzel (z. B. „RB") für den Avatar-Kreis. */
    kuerzel: string | null;
    /** Heutiger Status: anwesend oder Abwesenheits-Typ. */
    status: PresenceStatus;
    /** Ende der Abwesenheit ("bis wann"), ISO oder null. */
    until: string | null;
}

/** Ergebnis von `getAbsencesToday()` — Roster + Abwesende + Zusammenfassung (AP-A, DD10). */
export interface AbsencesTodayResult {
    /** Heute abwesende Mitarbeiter (nur Abwesende, DD10). */
    absences: DashboardAbsence[];
    /** Vollständiges Roster mit Präsenz-Status (für die Karte). */
    people: PersonPresence[];
    /** Anzahl heute Abwesender. */
    absent: number;
    /** Anzahl betrachteter Mitarbeiter (Postfächer). */
    total: number;
}

/** Roh-Form von `GET /dashboard/absences/today` (DD10). */
interface RawAbsencesToday {
    absences?: DashboardAbsence[] | null;
    people?: PersonPresence[] | null;
    summary?: { absent?: number | null; total?: number | null } | null;
}

/** Fachlicher Bearbeitungsstatus eines Risiko-Objekts (AP-Ri, DD5/DD7). */
export type RiskStatus = "entwurf" | "in_bearbeitung" | "aktiv" | "abgeschlossen";

/** Ergebnis von `getRiskCountByStatus()` — Status-Aufschlüsselung + Gesamtzahl. */
export interface RiskCountByStatusResult {
    /** Anzahl je vorkommendem Status (Schlüssel = Status-String, z. B. „in_bearbeitung"). */
    byStatus: Record<string, number>;
    /** Gesamtzahl aller Risiken im Mandanten (KPI). */
    total: number;
}

/** Roh-Form von `GET /risk-objects/count/by-status` (AP-Ri, DD5/DD7). */
interface RawRiskCountByStatus {
    byStatus?: Record<string, number> | null;
    total?: number | null;
}

/** Ein Kontakt mit Geburtstag heute (AP-G, DD1/DD9). */
export interface DashboardBirthday {
    /** Kontakt-Id (für die Verlinkung auf die Kontakt-Detailseite). */
    id: number;
    /** Anzeigename („Nachname, Vorname" bzw. Firma). */
    name: string;
    /** Geburtsdatum (ISO `YYYY-MM-DD`). */
    birthDate: string;
    /** Erreichtes Alter heute, sofern berechenbar (sonst null). */
    age: number | null;
    /** Kontaktstatus-Label („Kunde"/„Interessent") — fürs Status-Chip (P5). */
    statusLabel: string;
}

/** Roh-Form von `GET /dashboard/birthdays/today` (AP-G, DD1/DD9). */
interface RawBirthday {
    id?: number | null;
    name?: string | null;
    birthDate?: string | null;
    age?: number | null;
    statusLabel?: string | null;
}

/** Anzeigename eines Kontakts (bevorzugt `displayName`, sonst zusammensetzen). */
const contactDisplayName = (c: RawContact): string => {
    if (c.displayName) return String(c.displayName);
    const parts = [c.lastname, c.firstName].filter(Boolean);
    if (parts.length) return parts.join(", ");
    return c.company ? String(c.company) : "—";
};

/** „Letzte Aktivität" als Millis (für die absteigende Sortierung). */
const lastActivityMs = (c: RawContact): number => {
    const raw = c.lastModified ?? c.created ?? null;
    const t = raw ? Date.parse(raw) : NaN;
    return Number.isNaN(t) ? 0 : t;
};

/**
 * Offene Schäden für die Schäden-Karte (AP-S, DD6/DD8).
 *
 * Reuse des bestehenden `GET /damage?status=offen`-Endpunkts (kein neuer
 * Backend-Endpunkt, N4). Da der Damage-List-Endpunkt keinen `?scope`-Parameter
 * hat, holen wir eine größere Charge offener Schäden und filtern Meine/Team
 * clientseitig über die mitgelieferten Ownership-Felder; KPI = Anzahl im Scope,
 * Liste = die ersten `limit`.
 */
export const dashboardService = {
    async getOpenDamages(
        scope: DashScope,
        ctx: { userId: string | null; teammates: string[]; teamIds: string[] },
        limit = 5,
    ): Promise<OpenDamagesResult> {
        // Genug Zeilen ziehen, damit der clientseitige Scope-Count belastbar ist.
        const { data } = await apiFetch<RawDamage[]>("damage", {
            params: { status: "offen", limit: 500 },
        });
        const rows = Array.isArray(data) ? data : [];
        const scoped = rows.filter((d) => matchesScope(d, scope, ctx));
        const items: DashboardDamage[] = scoped.slice(0, Math.max(1, limit)).map((d) => ({
            id: d.id,
            damageNbr: String(d.damageNbr ?? d.externalDamageNbr ?? ""),
            title: String(d.title ?? ""),
            damageDate: d.damageDate ?? null,
            contactName: contactName(d.contact),
            contactId: d.contact?.id ?? null,
        }));
        return { items, count: scoped.length };
    },

    /**
     * Meine/Team-Kunden für die Meine-Kunden-Karte (AP-K, DD11/DD8).
     *
     * Reuse des bestehenden `GET /contact?scope=own|team`-Endpunkts (kein neuer
     * Backend-Endpunkt). Der KPI-Zähler kommt aus dem `X-Total-Count`-Header
     * (ungefilterte Scope-Gesamtzahl); die Top-`limit`-Liste wird clientseitig
     * nach letzter Aktivität sortiert (`lastModified` bzw. `created`), da der
     * Endpunkt keinen serverseitigen `?sort=lastActivity` anbietet. Es wird eine
     * größere Charge gezogen, damit die clientseitige Top-N-Sortierung über die
     * jüngsten Kontakte belastbar ist.
     */
    async getMyContacts(
        scope: DashScope | "all",
        limit = 5,
        filters?: { boTeamIds?: string[]; foUserIds?: string[] },
    ): Promise<MyContactsResult> {
        // gfgl-Filter (P6): boTeamIds/foUserIds als `filters`-JSON anhängen — gleiche
        // Mechanik wie die Kontakte-Tabelle (Backend filtert boTeamId/foUserId IN (…)).
        const params: Record<string, string | number | boolean> = { scope, include: "", limit: 200, offset: 0 };
        const f: Record<string, unknown> = {};
        if (filters?.boTeamIds?.length) f.boTeamIds = filters.boTeamIds;
        if (filters?.foUserIds?.length) f.foUserIds = filters.foUserIds;
        if (Object.keys(f).length) params.filters = JSON.stringify(f);
        const { data, total } = await apiFetch<RawContact[]>("contact", { params });
        const rows = Array.isArray(data) ? data : [];
        const items: DashboardContact[] = [...rows]
            .sort((a, b) => lastActivityMs(b) - lastActivityMs(a))
            .slice(0, Math.max(1, limit))
            .map((c) => ({
                id: c.id,
                displayName: contactDisplayName(c),
                typeLabel: String(c.type?.type ?? ""),
                lastActivity: c.lastModified ?? c.created ?? null,
            }));
        // KPI bevorzugt den ungefilterten Scope-Total aus dem Header; Fallback
        // auf die Anzahl gelieferter Zeilen, falls der Header fehlt.
        const count = total !== null ? total : rows.length;
        return { items, count };
    },

    /**
     * Heute abwesende Mitarbeiter für die An-/Abwesenheiten-Karte (AP-A, DD10).
     *
     * Liest den vom täglichen Job befüllten Cache über den dedizierten
     * Aggregations-Endpunkt `GET /dashboard/absences/today`. Geliefert werden nur
     * die heute Abwesenden (Name + Typ + „bis wann") plus die Zusammenfassung
     * „X von Y abwesend". Bei leerem/ nicht konfiguriertem Cache (z. B. Graph-App-
     * Permission noch nicht eingerichtet) kommt eine leere Liste zurück — die
     * Karte zeigt dann sauber „Alle anwesend".
     */
    async getAbsencesToday(): Promise<AbsencesTodayResult> {
        const { data } = await apiFetch<RawAbsencesToday>("dashboard/absences/today");
        const absences = Array.isArray(data?.absences) ? data.absences : [];
        const people = Array.isArray(data?.people) ? data.people : [];
        const absent = data?.summary?.absent ?? absences.length;
        const total = data?.summary?.total ?? people.length;
        return { absences, people, absent, total };
    },

    /**
     * Risiken je Bearbeitungsstatus für die Risiken-Karte (AP-Ri, DD5/DD7).
     *
     * Reuse des bestehenden, mandanten-scoped Aggregations-Endpunkts
     * `GET /risk-objects/count/by-status` (#457). Liefert die Anzahl je Status
     * (z. B. „in_bearbeitung"/„entwurf") plus die Gesamtzahl. Der Endpunkt zählt
     * tenantweit (keine Owner/Team-Aufschlüsselung im Aggregat), daher zeigt die
     * Karte die tenantweiten Zahlen — eine Meine/Team-Aufschlüsselung ist
     * Folge-Detail (siehe PR-Body).
     */
    async getRiskCountByStatus(): Promise<RiskCountByStatusResult> {
        const { data } = await apiFetch<RawRiskCountByStatus>("risk-objects/count/by-status");
        const byStatus =
            data?.byStatus && typeof data.byStatus === "object" ? data.byStatus : {};
        const total =
            typeof data?.total === "number"
                ? data.total
                : Object.values(byStatus).reduce((sum, n) => sum + (Number(n) || 0), 0);
        return { byStatus, total };
    },

    /**
     * Kontakte mit Geburtstag heute für die Geburtstage-Karte (AP-G, DD1/DD9).
     *
     * Liest den dedizierten, mandanten-scoped Aggregations-Endpunkt
     * `GET /dashboard/birthdays/today` (serverseitiger Tag/Monat-Filter über
     * `Contact.birthDate`). Nur Kunden/Kontakte — keine Mitarbeiter-Geburtstage.
     */
    async getBirthdaysToday(): Promise<DashboardBirthday[]> {
        const { data } = await apiFetch<RawBirthday[]>("dashboard/birthdays/today");
        const rows = Array.isArray(data) ? data : [];
        return rows
            .filter((b): b is RawBirthday & { id: number } => typeof b?.id === "number")
            .map((b) => ({
                id: b.id,
                name: String(b.name ?? "—"),
                birthDate: String(b.birthDate ?? ""),
                age: typeof b.age === "number" ? b.age : null,
                statusLabel: String(b.statusLabel ?? ""),
            }));
    },
};

export default dashboardService;
